import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

export default function TaskTimelineView({ tasks = [], onTaskClick }) {
  const [timelineStart, setTimelineStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 3);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const totalDays = 21; // 3-week window
  const days = [];
  for (let i = 0; i < totalDays; i++) {
    const d = new Date(timelineStart);
    d.setDate(d.getDate() + i);
    days.push(d);
  }

  const handlePrev = () => {
    const next = new Date(timelineStart);
    next.setDate(next.getDate() - 7);
    setTimelineStart(next);
  };

  const handleNext = () => {
    const next = new Date(timelineStart);
    next.setDate(next.getDate() + 7);
    setTimelineStart(next);
  };

  const handleToday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 3);
    d.setHours(0, 0, 0, 0);
    setTimelineStart(d);
  };

  const timelineStartTime = timelineStart.getTime();
  const timelineEndTime = timelineStartTime + totalDays * 86400000;

  const getTaskBarPosition = (task) => {
    if (!task.start_at && !task.end_at) return null;

    const start = task.start_at ? new Date(task.start_at).getTime() : new Date(task.end_at).getTime() - 86400000;
    const end = task.end_at ? new Date(task.end_at).getTime() : start + 86400000;

    // Check if within visible range
    if (end < timelineStartTime || start > timelineEndTime) return null;

    const clampedStart = Math.max(start, timelineStartTime);
    const clampedEnd = Math.min(end, timelineEndTime);

    const leftPercent = ((clampedStart - timelineStartTime) / (timelineEndTime - timelineStartTime)) * 100;
    const widthPercent = Math.max(2, ((clampedEnd - clampedStart) / (timelineEndTime - timelineStartTime)) * 100);

    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
    };
  };

  const isToday = (d) => {
    const today = new Date();
    return (
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear()
    );
  };

  return (
    <div className="task-timeline-wrapper">
      {/* Controls */}
      <div className="task-timeline-controls">
        <div className="task-timeline-controls-left" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            Timeline & Schedule
          </h2>
          <button onClick={handleToday} className="btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}>
            Jump to Today
          </button>
        </div>

        <div className="task-timeline-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={handlePrev}
            style={{
              background: 'transparent',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
              borderRadius: '8px',
              padding: '6px',
              cursor: 'pointer',
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
            {days[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} –{' '}
            {days[days.length - 1].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <button
            onClick={handleNext}
            style={{
              background: 'transparent',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
              borderRadius: '8px',
              padding: '6px',
              cursor: 'pointer',
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Timeline Gantt Grid */}
      <div className="task-timeline-container" style={{ display: 'flex', overflowX: 'auto' }}>
        {/* Left Column: Task Titles */}
        <div
          style={{
            width: '280px',
            minWidth: '280px',
            borderRight: '1px solid var(--border-color)',
            background: 'var(--bg-surface-solid)',
          }}
        >
          <div
            style={{
              height: '42px',
              borderBottom: '1px solid var(--border-color)',
              padding: '10px 16px',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            TASKS ({tasks.length})
          </div>

          {tasks.map((task) => (
            <div
              key={task.id}
              onClick={() => onTaskClick && onTaskClick(task)}
              style={{
                height: '48px',
                borderBottom: '1px solid var(--border-color)',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={task.title}
            >
              <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {task.title}
              </span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: task.progress === 100 ? 'var(--success)' : 'var(--accent-primary)',
                  marginLeft: '8px',
                }}
              >
                {task.progress}%
              </span>
            </div>
          ))}
        </div>

        {/* Right Section: Time Bars */}
        <div style={{ flex: 1, minWidth: '700px', display: 'flex', flexDirection: 'column' }}>
          {/* Days Header */}
          <div
            style={{
              height: '42px',
              borderBottom: '1px solid var(--border-color)',
              display: 'grid',
              gridTemplateColumns: `repeat(${totalDays}, 1fr)`,
              background: 'rgba(255, 255, 255, 0.02)',
            }}
          >
            {days.map((d, idx) => {
              const today = isToday(d);
              return (
                <div
                  key={idx}
                  style={{
                    borderRight: '1px solid rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    color: today ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    fontWeight: today ? 700 : 400,
                    background: today ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                  }}
                >
                  <span>{d.toLocaleDateString(undefined, { weekday: 'narrow' })}</span>
                  <span>{d.getDate()}</span>
                </div>
              );
            })}
          </div>

          {/* Rows corresponding to each task */}
          {tasks.map((task) => {
            const barPos = getTaskBarPosition(task);

            return (
              <div
                key={task.id}
                onClick={() => onTaskClick && onTaskClick(task)}
                style={{
                  height: '48px',
                  borderBottom: '1px solid var(--border-color)',
                  display: 'grid',
                  gridTemplateColumns: `repeat(${totalDays}, 1fr)`,
                  position: 'relative',
                  cursor: 'pointer',
                  background: 'transparent',
                }}
              >
                {/* Vertical grid lines */}
                {days.map((d, idx) => (
                  <div
                    key={idx}
                    style={{
                      borderRight: '1px solid rgba(255, 255, 255, 0.03)',
                      background: isToday(d) ? 'rgba(59, 130, 246, 0.03)' : 'transparent',
                    }}
                  />
                ))}

                {/* Horizontal Gantt Bar */}
                {barPos && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '10px',
                      height: '28px',
                      left: barPos.left,
                      width: barPos.width,
                      borderRadius: '6px',
                      background:
                        task.status === 'COMPLETED'
                          ? 'rgba(16, 185, 129, 0.3)'
                          : task.priority === 'URGENT'
                          ? 'rgba(239, 68, 68, 0.3)'
                          : 'rgba(59, 130, 246, 0.3)',
                      border: `1px solid ${
                        task.status === 'COMPLETED'
                          ? 'var(--success)'
                          : task.priority === 'URGENT'
                          ? 'var(--danger)'
                          : 'var(--accent-primary)'
                      }`,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 8px',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                    }}
                  >
                    {/* Progress fill */}
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${task.progress}%`,
                        background:
                          task.status === 'COMPLETED'
                            ? 'rgba(16, 185, 129, 0.6)'
                            : task.priority === 'URGENT'
                            ? 'rgba(239, 68, 68, 0.6)'
                            : 'rgba(59, 130, 246, 0.6)',
                        zIndex: 1,
                      }}
                    />
                    <span
                      style={{
                        position: 'relative',
                        zIndex: 2,
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#fff',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {task.title}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
